import { ForbiddenError, MissingTokenError, UnauthorizedError } from '@einwert/valuation-api-client'
import { Mutation, MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { UnsupportedQueryKeyFormat } from 'ClientPortal/src/api/errors'
import { sendDebugInfoToSentry } from 'ComponentLibrary'
import { isArray } from 'lodash'

type InvalidateQueryCachesProps = {
    invalidateQueries: QueryClient['invalidateQueries']
    meta: Mutation['meta']
    variables?: unknown
}

/**
 * Invalidates React Query caches based on provided metadata and variables.
 * Supports both single query key arrays and arrays of query keys.
 *
 * @param props - Object containing invalidation parameters
 * @param props.meta - Metadata object containing invalidation rules
 * @param props.meta.invalidates - Query key(s) to invalidate. Can be a single query key array or array of query key arrays
 * @param props.variables - Variables object passed to any functions in query keys
 * @param props.invalidateQueries - Function to perform the actual cache invalidation
 *
 * @throws {UnsupportedQueryKeyFormat} When query keys are not provided in array format
 *
 * @example
 * // Single query key invalidation
 * invalidateQueryCaches({
 *   meta: { invalidates: ['users', ({variables}) => variables.userId] },
 *   variables: { userId: '123' },
 *   invalidateQueries
 * });
 *
 * @example
 * // Multiple query keys invalidation
 * invalidateQueryCaches({
 *   meta: {
 *     invalidates: [
 *       ['users', ({variables}) => variables.userId],
 *       ['profiles', ({variables}) => variables.userId]
 *     ]
 *   },
 *   variables: { userId: '123' },
 *   invalidateQueries
 * });
 */
export function invalidateQueryCaches({ meta, variables, invalidateQueries }: InvalidateQueryCachesProps): Promise<unknown> | undefined {
    if (meta?.invalidates === undefined || meta?.invalidates === null) {
        return
    }

    if (!isArray(meta.invalidates)) {
        throw new UnsupportedQueryKeyFormat(`All query keys must be contained in an array, received: ${meta.invalidates}`)
    }

    const isTwoDimensionalArray = meta.invalidates.every((item) => Array.isArray(item))

    if (isTwoDimensionalArray) {
        const promises = meta.invalidates.map((key) => {
            return invalidateQueries({ queryKey: processQueryKey(key, variables) })
        })
        return Promise.all(promises)
    }

    return invalidateQueries({ queryKey: processQueryKey(meta.invalidates, variables) })
}

/**
 * Processes a query key array by executing any functions it contains.
 * Functions in the query key receive a context object with variables.
 *
 * @param queryKey - Array containing query key segments (strings, numbers, functions, etc.)
 * @param variables - Optional variables object passed to functions in query key
 * @returns New array with function results replacing the original functions
 *
 * @example
 *
 * const key = ["users", ({variables}) => `${variables.userId}-profile`, "details"];
 * const processed = processQueryKey(key, { userId: "123" });
 * // Result: ["users", "123-profile", "details"]
 */
const processQueryKey = (queryKey: unknown[], variables?: unknown): unknown[] => {
    return queryKey.map((item) => {
        if (typeof item === 'function') {
            return item({ variables })
        }
        return item
    })
}

type ExtractedValue = string

type VariableExtractorArguments<TVariables> = { variables: TVariables }
type VariableExtractor<TVariables> = ({ variables }: VariableExtractorArguments<TVariables>) => ExtractedValue

export function mutationVariable<TVariables extends Record<K, unknown>, K extends string>(key: K): VariableExtractor<TVariables> {
    return ({ variables }: VariableExtractorArguments<TVariables>) => {
        const extractedKey = variables[key]

        if (typeof extractedKey !== 'string') {
            throw new UnsupportedQueryKeyFormat(`key extracted from variables must be of type string, received: ${typeof extractedKey}`)
        }
        return extractedKey
    }
}

export function throwOnForbiddenApiError(error: unknown) {
    return isForbiddenApiError(error)
}

const RETRY_THRESHOLD = 3

function shouldAttemptRetry(failureCount: number, error: unknown): boolean {
    return failureCount < RETRY_THRESHOLD && shouldRetryOnError(error)
}

function shouldRetryOnError(error: unknown): boolean {
    return !isForbiddenApiError(error)
}

function isForbiddenApiError(error: unknown): error is ForbiddenError {
    return error instanceof ForbiddenError
}

type QueryClientFactoryParams = {
    handleError: (error: unknown) => void
    redirectToLogin: () => void
}

export const QueryClientSingletonFactory = (function () {
    let instance: QueryClient | undefined

    function createQueryClient({ handleError, redirectToLogin }: QueryClientFactoryParams): QueryClient {
        const queryClient = new QueryClient({
            mutationCache: new MutationCache({
                onError: handleError,
            }),
            queryCache: new QueryCache({
                onError: (error) => {
                    if (error instanceof MissingTokenError || error instanceof UnauthorizedError) {
                        sendDebugInfoToSentry(error)
                        redirectToLogin()
                    }
                },
            }),
            defaultOptions: {
                queries: {
                    retry: shouldAttemptRetry,
                },
            },
        })
        queryClient.getMutationCache().config.onSuccess = async (_data, variables, _context, mutation) => {
            try {
                return invalidateQueryCaches({
                    meta: mutation.meta,
                    variables,
                    invalidateQueries: queryClient.invalidateQueries.bind(queryClient),
                })
            } catch (error) {
                sendDebugInfoToSentry(error)
            }
        }
        return queryClient
    }

    return {
        getInstance({ handleError, redirectToLogin }: QueryClientFactoryParams) {
            if (instance === undefined) {
                instance = createQueryClient({ handleError, redirectToLogin })
            }
            return instance
        },
    }
})()
